if (typeof window !== 'undefined') {
  if (!window.requestIdleCallback) {
    (window as any).requestIdleCallback = function(cb: IdleRequestCallback) {
      const start = Date.now()
      return window.setTimeout(function() {
        cb({
          didTimeout: false,
          timeRemaining: function() {
            return Math.max(0, 50 - (Date.now() - start))
          }
        })
      }, 1)
    }
    window.cancelIdleCallback = function(id) {
      clearTimeout(id)
    }
  }
}

import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { SpeedInsights } from "@vercel/speed-insights/react";
import App from "./App.tsx";
import "./index.css";
import './styles/iq-v2-fonts.css';
import { initPostHog } from "./lib/posthog";
import { initSentry } from "./lib/sentry";

initSentry();
initPostHog();

const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const isPreviewHost =
  window.location.hostname.includes("vercel.app");

if (isPreviewHost || isInIframe) {
  navigator.serviceWorker?.getRegistrations().then(async (registrations) => {
    if (registrations.length === 0) return;
    await Promise.all(registrations.map((r) => r.unregister()));
    // A legacy service worker was still controlling this page and can serve
    // stale chunks / intercept fetches (e.g. a hung briefing-compute call).
    // Reload once (guarded against a loop) so we run free of the SW.
    if (
      navigator.serviceWorker.controller &&
      !sessionStorage.getItem("sw-unregistered-reload")
    ) {
      sessionStorage.setItem("sw-unregistered-reload", "1");
      window.location.reload();
    }
  });
} else if ("serviceWorker" in navigator) {
  // Was the page already SW-controlled at load? Used to avoid a spurious reload on
  // the very first install (when clientsClaim first takes control).
  const hadControllerAtLoad = !!navigator.serviceWorker.controller;

  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then((reg) => {
      reg.addEventListener("updatefound", () => {
        const newWorker = reg.installing;
        newWorker?.addEventListener("statechange", () => {
          if (
            newWorker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            window.dispatchEvent(new Event("sw-update-available"));
          }
        });
      });
      // Proactively look for a new deploy. The browser only auto-checks on navigation
      // (~daily), so an always-open dock PWA would otherwise run stale code until a
      // manual restart. Check whenever the app regains focus, and every 15 min. With
      // skipWaiting+clientsClaim, a found update activates and fires controllerchange
      // (below), reloading onto the new build.
      const checkForUpdate = () => { reg.update().catch(() => {}); };
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") checkForUpdate();
      });
      setInterval(checkForUpdate, 15 * 60 * 1000);
    });
  });

  // A new SW taking control means a new deploy activated → reload onto it once.
  // Guard against loops, and skip the first-install claim (no prior controller).
  let reloadingForUpdate = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloadingForUpdate || !hadControllerAtLoad) return;
    reloadingForUpdate = true;
    window.location.reload();
  });
}

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
    <SpeedInsights />
  </HelmetProvider>
);
